import config from "../../config.json";
import { reportApiError } from "../errors/presentError";
const BASE_AUTH_API = config.BASE_AUTH_API;

const normalizeCRM = (record) => {
  if (!record || record.onboarding) return record;
  const legacy = record.legacy || {};
  return {
    ...record,
    onboarding: {
      _id: "",
      store: {
        businessName: legacy.businessName || "",
        city: legacy.city || "",
        branchCount: legacy.branchNumber || 1,
        ownerName: legacy.owner?.name || "",
        contactNumber: legacy.owner?.contactNumber || "",
        email: legacy.owner?.email || "",
        branches: [],
      },
      contact: {
        contactName: legacy.contactPerson?.name || "",
        jobTitle: legacy.contactPerson?.role || "",
        mobile: legacy.contactPerson?.contactNumber || "",
        email: legacy.contactPerson?.email || "",
      },
      entity: {
        entityName: legacy.businessInfo?.entityName || "",
        commercialRegistration: legacy.businessInfo?.commercialRegistration || "",
        vatNumber: legacy.businessInfo?.taxNumber || "",
        crExpiration: "",
      },
      documents: { files: [], notes: legacy.notes?.text || "" },
      quotation: {},
    },
  };
};

// Public registration (/contactus). Never throws and never shows a dialog: the caller decides what the visitor sees.
//   ok         the registration exists (created now, or an earlier identical attempt - `replayed`)
//   status     HTTP status (0 = no answer: offline, timeout, CORS)
//   code       "ACCOUNT_EXISTS" | "IDENTITY_CONFLICT" | "INVALID_EMAIL" | "INVALID_INPUT" | undefined
// `data.registrationId` makes a retry idempotent: the same id never creates a second store or account.
export const addcrmAPI = async (data) => {
  try {
    const response = await fetch(`${BASE_AUTH_API}crm`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const ok = response.ok && body?.success === true;
    return {
      ok,
      status: response.status,
      code: body?.code,
      message: body?.message,
      replayed: Boolean(body?.replayed),
      invitationEmail: body?.invitationEmail,
      crm: ok ? normalizeCRM(body.crm) : undefined,
    };
  } catch (error) {
    return { ok: false, status: 0, network: true, message: error?.message };
  }
};
export const addNewcrmAPI = async (token, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/add`, {
      method: "POST",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return normalizeCRM(responseData.crm);
    } else {
      throw new Error(responseData.message || "Unable to create CRM customer");
    }
  } catch (error) {
    reportApiError(error);
    throw error;
  }
};
export const bulkCreateCRMAPI = async (token, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/bulk-crm`, {
      method: "POST",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return {
        insertedCount: responseData.insertedCount,
        unresolved: responseData.unresolved || [],
      };
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};
export const bulkAssignAPI = async (token, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/assign`, {
      method: "POST",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return responseData.modifiedCount;
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};
export const updatecrmAPI = async (token, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/${data.id || data._id}`, {
      method: "PUT",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data.payload || data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return normalizeCRM(responseData.crm);
    } else {
      throw new Error(responseData.message || "Unable to update CRM customer");
    }
  } catch (error) {
    reportApiError(error);
    throw error;
  }
};
export const addcrmToStoreAPI = async (token, id) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/addStore/${id}`, {
      method: "POST",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return responseData.store;
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};
export const updateStatuscrmAPI = async (token, id, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/${id}/status`, {
      method: "PATCH",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return normalizeCRM(responseData.crm);
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};
export const updateDatecrmAPI = async (token, id, data) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/${id}/date`, {
      method: "PATCH",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));
    if (responseData.success) {
      return normalizeCRM(responseData.crm);
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};

export const fetchcrmAPI = async (token) => {
  const url = `${BASE_AUTH_API}crm`;

  let responseData;
  try {
    await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/form-data",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));

    if (responseData.success) {
      return responseData.data.map(normalizeCRM);
    } else {
      return;
    }
  } catch (error) {
    reportApiError(error);
    return;
  }
};
export const deletecrmAPI = async (token, id) => {
  const response = await fetch(`${BASE_AUTH_API}crm/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Unable to delete CRM customer");
  }
  return data;
};
export const createCrmUserAPI = async (token, id) => {
  const response = await fetch(`${BASE_AUTH_API}crm/${id}/create-user`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Unable to create or link user");
  }
  return data;
};
export const resendCrmInvitationAPI = async (token, id) => {
  const response = await fetch(`${BASE_AUTH_API}crm/${id}/resend-invitation`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.message || "Unable to resend the invitation");
  }
  return data;
};
export const fetchAvailableOnboardingsAPI = async (token) => {
  const response = await fetch(`${BASE_AUTH_API}crm/onboardings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const result = await response.json();
  return result.success ? result.data : [];
};
export const fetchSalesUsersAPI = async (token) => {
  let responseData;
  try {
    await fetch(`${BASE_AUTH_API}crm/users`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => (responseData = data));

    if (responseData.success) {
      return responseData.users;
    } else {
      return;
    }
  } catch (error) {
    console.log(error.message);
    return;
  }
};
